import { Component, ElementRef, OnInit, SecurityContext, OnChanges, OnDestroy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, AuthError, InteractionStatus } from '@azure/msal-browser';
import { service, factories, models, IEmbedConfiguration } from 'powerbi-client';
import { ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ReportsService } from '../reports.service';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-powerbi-autologin',
    templateUrl: './powerbi-autologin.component.html',
    styleUrl: './powerbi-autologin.component.css'
})
export class PowerbiAutologinComponent implements OnChanges, OnDestroy, OnInit {
  powerbi = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory);
  state: any;
  reportContainer: any;
  context: any;
  accessToken: any;
  embedUrl: any;
  private _destroying$: ReplaySubject<boolean> = new ReplaySubject(1);
  ReportName: any;
  ReportID: any = '';
  EmbedUrl: any = '';
  useDirectIframeEmbed: boolean = false;
  private readonly allowedEmbedHosts = new Set([
    'app.powerbi.com',
    'msit.powerbi.com'
  ]);
  constructor(
    private msalService: MsalService,
    private broadcastService: MsalBroadcastService,
    private ContainerRef: ElementRef,
    private readonly reportsService: ReportsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly sanitizer: DomSanitizer
  ) {
    this.msalService.initialize();
    this.activatedRoute.queryParams.subscribe(data => {
      this.ReportName = data['ReportName'];
      this.ReportID = data['ReportID'] || this.ReportID;
      this.EmbedUrl = data['EmbedUrl'] || this.EmbedUrl;
      this.useDirectIframeEmbed = !!this.EmbedUrl;

      if (!this.ReportID && this.EmbedUrl) {
        this.ReportID = this.extractReportIdFromEmbedUrl(this.EmbedUrl);
      }
    });
  }

  // React function
  render() {
    this.reportContainer = this.ContainerRef.nativeElement.querySelector('#reportContainer');
    this.reportContainer.innerHTML = '';

    if (this.useDirectIframeEmbed && this.EmbedUrl) {
      this.renderDirectIframe(this.EmbedUrl);
      return;
    }

    const accessToken = this.accessToken;
    const embedUrl = this.embedUrl;
    const ReportID = this.ReportID;
    if (this.state?.error?.length) {
      // Cleaning the report container contents and rendering the error message in multiple lines
      this.state.error.forEach(line => {
        this.reportContainer.appendChild(document.createTextNode(line));
        this.reportContainer.appendChild(document.createElement('br'));
      });
    } else if (this.state.accessToken !== '' && this.state.embedUrl !== '') {
      const embedConfiguration: IEmbedConfiguration = {
        type: 'report',
        tokenType: models.TokenType.Aad,
        accessToken,
        embedUrl,
        permissions: models.Permissions.All,
        eventHooks: {
          accessTokenProvider: this.getNewAccessToken
        },
        id: ReportID,
        settings: {
          personalBookmarksEnabled: true,
          panes: {
            bookmarks: {
              visible: false
            },
            fields: {
              expanded: false
            },
            filters: {
              expanded: false,
              visible: false
            },
            pageNavigation: {
              visible: true
            },
            selection: {
              visible: true
            },
            syncSlicers: {
              visible: true
            },
            visualizations: {
              expanded: false
            }
          },
          bars: {
            actionBar: {
              visible: true
            },
            statusBar: {
              visible: true
            }
          }
        }
      };

      const report = this.powerbi.embed(this.reportContainer, embedConfiguration);

      // Clear any other loaded handler events
      report.off('loaded');

      // Triggers when a content schema is successfully loaded
      report.on('loaded', function() {
        console.log('Report load successful');
      });

      // Clear any other rendered handler events
      report.off('rendered');

      // Triggers when a content is successfully embedded in UI
      report.on('rendered', function() {
        console.log('Report render successful');
      });

      // Clear any other error handler event
      report.off('error');

      // Below patch of code is for handling errors that occur during embedding
      report.on('error', function(event) {
        const errorMsg = event.detail;

        // Use errorMsg variable to log error in any destination of choice
        console.error(errorMsg);
      });
    }
  }

  ngOnInit(): void {
    // User input - null check
    if (environment.workspaceId === '') {
      console.log('Please assign values to workspace Id in Config.ts file');
      return;
    }

    if (sessionStorage.getItem('ptoken')) {
      this.accessToken = sessionStorage.getItem('ptoken');
      this.loadReportByQueryContext();
    } else {
      this.authenticate();
    }
  }

  ngOnChanges(): void {
    if (sessionStorage.getItem('ptoken')) {
      this.accessToken = sessionStorage.getItem('ptoken');
      this.loadReportByQueryContext();
    } else {
      this.authenticate();
    }
  }

  private loadReportByQueryContext(): void {
    if (this.EmbedUrl) {
      this.embedUrl = this.EmbedUrl;
      if (!this.ReportID) {
        this.ReportID = this.extractReportIdFromEmbedUrl(this.EmbedUrl);
      }
      this.state = { accessToken: this.accessToken, embedUrl: this.embedUrl };
      this.render();
      return;
    }

    if (this.ReportID) {
      this.getembedUrl();
      return;
    }

    this.getReport(this.ReportName);
  }

  private renderDirectIframe(embedUrl: string): void {
    const safeUrl = this.getSafeEmbedUrl(embedUrl);
    if (!safeUrl) {
      this.state = { error: ['Error occurred while loading iframe report URL'] };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', this.ReportName || 'Power BI Report');
    iframe.setAttribute('src', safeUrl);
    iframe.setAttribute('width', '100%');
    iframe.setAttribute('height', '900');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', 'true');
    this.reportContainer.appendChild(iframe);
  }

  private getSafeEmbedUrl(embedUrl: string): string {
    const rawUrl = (embedUrl || '').trim();
    if (!rawUrl) {
      return '';
    }

    const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, rawUrl);
    if (!sanitizedUrl) {
      return '';
    }

    try {
      const parsedUrl = new URL(sanitizedUrl);
      const isHttps = parsedUrl.protocol === 'https:';
      const isTrustedHost = this.allowedEmbedHosts.has(parsedUrl.hostname.toLowerCase());

      if (!isHttps || !isTrustedHost) {
        return '';
      }

      return parsedUrl.toString();
    } catch {
      return '';
    }
  }

  private extractReportIdFromEmbedUrl(embedUrl: string): string {
    if (!embedUrl) {
      return '';
    }

    try {
      const parsedUrl = new URL(embedUrl);
      return parsedUrl.searchParams.get('reportId') || parsedUrl.searchParams.get('reportID') || '';
    } catch {
      return '';
    }
  }

  ngOnDestroy(): void {
    this.powerbi.reset(this.reportContainer);
  }

  // Authenticating to get the access token
  authenticate(): void {
    const msalInstance = this.msalService.instance;
    const msalAccounts = msalInstance.getAllAccounts();
    const msalInProgress = this.broadcastService.inProgress$;
    const isAuthenticated = msalInstance.getAllAccounts().length > 0;

    const eventCallback = msalInstance.addEventCallback((message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_SUCCESS && !this.accessToken) {
        const payload = message.payload as AuthenticationResult;
        const name: string = payload.account?.name ? payload.account?.name : '';

        this.accessToken = payload.accessToken;
        sessionStorage.setItem('ptoken', this.accessToken);
        this.setUsername(name);
        this.tryRefreshUserPermissions();
      }
    });

    const loginRequest = {
      scopes: environment.scopeBase,
      account: msalAccounts[0]
    };
    // handle auth redired/do all initial setup for msal
    msalInstance
      .handleRedirectPromise()
      .then(() => {
        // Check if user signed in
        const account = msalInstance.getAllAccounts();
        if (!account) {
          // redirect anonymous user to login page
          msalInstance.loginRedirect(loginRequest);
        }
      })
      .catch(err => {
        // TODO: Handle errors
        console.log(err);
      });
    msalInProgress
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(async () => {
        if (!isAuthenticated) {
          await msalInstance.loginRedirect(loginRequest);
        } else if (isAuthenticated && this.accessToken && !this.embedUrl) {
          this.loadReportByQueryContext();
          // Remove the event callback to prevent memory leaks
          msalInstance.removeEventCallback(eventCallback);
        } else if (isAuthenticated && !this.accessToken && !this.embedUrl) {
          this.setUsername(msalAccounts[0].name);

          // get access token silently from cached id-token
          msalInstance
            .acquireTokenSilent(loginRequest)
            .then((response: AuthenticationResult) => {
              this.accessToken = response.accessToken;
              sessionStorage.setItem('ptoken', this.accessToken);
              this.loadReportByQueryContext();
            })
            .catch((error: AuthError) => {
              // Refresh access token silently from cached id-token
              // Makes the call to handleredirectcallback
              if (
                error.errorCode === 'consent_required' ||
                error.errorCode === 'interaction_required' ||
                error.errorCode === 'login_required'
              ) {
                msalInstance.acquireTokenRedirect(loginRequest);
              } else if (error.errorCode === '429') {
                console.log('Our Service Token Server (STS) is overloaded, please try again in sometime');
              } else {
                console.log('There was some problem fetching the access token' + error.toString());
              }
            });
        }
      });
  }

  // Power BI REST API call to refresh User Permissions in Power BI
  // Refreshes user permissions and makes sure the user permissions are fully updated
  // https://docs.microsoft.com/rest/api/power-bi/users/refreshuserpermissions
  tryRefreshUserPermissions(): void {
    fetch(environment.powerBiApiUrl + 'v1.0/myorg/RefreshUserPermissions', {
      headers: {
        Authorization: 'Bearer ' + this.accessToken
      },
      method: 'POST'
    })
      .then(function(response) {
        if (response.ok) {
          console.log('User permissions refreshed successfully.');
        } else {
          // Too many requests in one hour will cause the API to fail
          if (response.status === 429) {
            console.error('Permissions refresh will be available in up to an hour.');
          } else {
            console.error(response);
          }
        }
      })
      .catch(function(error) {
        console.error('Failure in making API call.' + error);
      });
  }

  // Power BI REST API call to get the embed URL of the report
  getembedUrl() {
    const myorgReport = 'v1.0/myorg/reports/';
    const url = environment.powerBiApiUrl + myorgReport + this.ReportID;
    fetch(url, {
      headers: {
        Authorization: 'Bearer ' + this.accessToken
      },
      method: 'GET'
    })
      .then(response => {
        const errorMessage: string[] = [];
        errorMessage.push('Error occurred while fetching the embed URL of the report');
        errorMessage.push('Request Id: ' + response.headers.get('requestId'));
        response
          .json()
          .then(body => {
            console.log('Embed URL fetched successfully.', response);
            // Successful response
            if (response.ok) {
              this.embedUrl = body['embedUrl'];
              this.state = { accessToken: this.accessToken, embedUrl: this.embedUrl };
              this.render();
            }
            // If error message is available
            else {
              errorMessage.push('Error ' + response.status + ': ' + body.error.code);
              this.state = { error: errorMessage };
              this.render();
              if (body['error']['code'] === 'TokenExpired') {
                this.getNewAccessToken();
              }
            }
          })
          .catch(e => {
            console.log(e);
            errorMessage.push('Error ' + response.status + ':  An error has occurred');
            this.state = { error: errorMessage };
            this.render();
            if (e['error'].code === 'TokenExpired') {
              this.getNewAccessToken();
            }
          });
      })
      .catch(error => {
        // Error in making the API call
        this.state = { error: error };
        this.render();
      });
  }

  // Show username in the UI
  setUsername(username: string): void {
    const welcome = document.getElementById('welcome');
    if (welcome !== null) welcome.innerText = 'Welcome, ' + username;
  }
  getNewAccessToken = async function() {
    const msalInstance = this.msalService.instance;
    const msalAccounts = msalInstance.getAllAccounts();
    const loginRequest = {
      scopes: environment.scopeBase,
      account: msalAccounts[0]
    };
    // get access token silently from cached id-token
    msalInstance
      .acquireTokenSilent(loginRequest)
      .then((response: AuthenticationResult) => {
        this.accessToken = response.accessToken;
        sessionStorage.setItem('ptoken', this.accessToken);
        this.getreport(this.reportName);
      })
      .catch((error: AuthError) => {
        // Refresh access token silently from cached id-token
        // Makes the call to handleredirectcallback
        if (
          error.errorCode === 'consent_required' ||
          error.errorCode === 'interaction_required' ||
          error.errorCode === 'login_required'
        ) {
          msalInstance.acquireTokenRedirect(loginRequest);
        } else if (error.errorCode === '429') {
          console.log('Our Service Token Server (STS) is overloaded, please try again in sometime');
        } else {
          console.log('There was some problem fetching the access token' + error.toString());
        }
      });
  };
  getReport(name) {
    this.reportsService.getReport(name).subscribe(result => {
      if (result.length > 0) {
        this.ReportID = result[0].Report_Id;
        this.getembedUrl();
      }
    });
  }
}
